import React, { useState, useEffect, useRef } from 'react';
import { StaticQuery, graphql, navigate } from 'gatsby';
import { useSpring, animated } from 'react-spring';
import { IoIosClose } from 'react-icons/io'; // Import the close icon
import { Box, Button } from 'theme-ui'; // Import Box and Button for styling
import get from 'lodash/get';
import SEO from '../components/seo';
import SecondaryLayout from "../components/layout/secondary/secondary";
import HomeBanner from '../components/home-banner/home-banner';
import CategoryBlocks from '../components/category-blocks/secondary/secondary';
import CallusBanner from '../components/call-us-banner/call-us-banner';
import AllProducts from "../components/all-products/all-products";

const indexPageStaticQuery = graphql`
    query {
        prismic {
            allChat2orderhomes {
                edges {
                    node {
                        banner
                        banner_button_text
                        banner_subtitle
                        banner_title
                        category_section_title
                        category_section_short_intro						
                        category_block {
                            category_slug
                            category_title
                            image
                            imageSharp {
                                childImageSharp {
                                    fluid {
                                        ...GatsbyImageSharpFluid_withWebp_tracedSVG
                                    }
                                }
                            }
                        }						
                        call_us_banner
                        call_us_button_text
                        call_us_title
                        call_us_subtitle
                        view_more_text
                    }
                }
            }
        }
    }
`;

const IndexPage: React.FC<{}> = () => {
    const [showOverlay, setShowOverlay] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const searchSectionRef = useRef<HTMLDivElement>(null); // Reference to the search section

    // Animation for the overlay
    const overlayAnimation = useSpring({
        from: { transform: 'translateY(100%)', opacity: 0 },
        to: { transform: showOverlay ? 'translateY(0%)' : 'translateY(100%)', opacity: showOverlay ? 1 : 0 },
        config: { tension: 200, friction: 20 },
    });

    // Function to scroll to the search section with an offset
    const scrollToSearchSection = () => {
        if (searchSectionRef.current) {
            //searchSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            var headerOffset = window.innerHeight * 0.1;
            var elementPosition = searchSectionRef.current.offsetTop;
            var offsetPosition = elementPosition + window.pageYOffset - headerOffset;            
            // Add an offset of 10vh
            window.scrollBy({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    // Close the overlay and scroll to the search section
    const handleCloseOverlay = () => {
        setShowOverlay(false);
        scrollToSearchSection();
    };

    return (
        <>
            {/* Animated Overlay */}
            {showOverlay && (
                <animated.div
                    style={{
                        ...overlayAnimation,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        color: 'white',
                    }}
                >
                    {/* Close Button */}
                    <button
                        title="Close"
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            fontSize: '32px',
                            cursor: 'pointer',
                        }}
                        onClick={handleCloseOverlay}
                    >
                        <IoIosClose />
                    </button>

                    {/* Search Bar */}
                    <Box
                        as="label"
                        style={{
                            marginTop: '10vh', // Position the search bar about one-third from the top
                            width: '80%',
                            maxWidth: '600px',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '10px',
                        }}
                    >
                        <svg
                            className="search-icon"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            style={{ marginRight: '10px' }}
                        >
                            <path
                                d="M16.122,12.452a6.772,6.772,0,1,0-1.195,1.2l.036.038,3.592,3.592a.847.847,0,0,0,1.2-1.2L16.16,12.488ZM14.364,4.706a5.079,5.079,0,1,1-7.183,0A5.079,5.079,0,0,1,14.364,4.706Z"
                                transform="translate(-4 -1.525)"
                                fillRule="evenodd"
                            />
                        </svg>
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="What Are You Looking For?"
                            autoComplete="off"
                            style={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                fontSize: '16px',
                            }}
                        />
                        {searchValue && (
                            <>
                                {/* Search Icon */}
                                <svg
                                    className="search-icon"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    style={{ marginRight: '10px', cursor: 'pointer' }}
                                    onClick={() => {
                                        console.log('Search triggered for:', searchValue);
                                        navigate('/aisearch',{state: { searchString: searchValue}});                                       
                                    }}
                                >
                                    <path
                                        d="M16.122,12.452a6.772,6.772,0,1,0-1.195,1.2l.036.038,3.592,3.592a.847.847,0,0,0,1.2-1.2L16.16,12.488ZM14.364,4.706a5.079,5.079,0,1,1-7.183,0A5.079,5.079,0,0,1,14.364,4.706Z"
                                        transform="translate(-4 -1.525)"
                                        fill="black"
                                    />
                                </svg>

                                {/* Close Icon */}
                                <svg
                                    className="close-icon"
                                    onClick={() => setSearchValue('')}
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <path
                                        d="M6.115,4.792A.936.936,0,0,0,4.792,6.115l5.4,5.4-5.4,5.4a.936.936,0,0,0,1.323,1.323l5.4-5.4,5.4,5.4a.936.936,0,0,0,1.323-1.323l-5.4-5.4,5.4-5.4a.936.936,0,1,0-1.323-1.323l-5.4,5.4Z"
                                        transform="translate(-4.518 -4.518)"
                                    />
                                </svg>
                            </>
                        )}
                    </Box>

                    {/* Pre-Populated Search Suggestion */}
                    <Button
                        onClick={() => setSearchValue('gift for 45 year old man who loves football')}
                        sx={{
                            marginTop: '20px',
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '14px',
                            padding: '10px 20px',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            border: 'none',
                        }}
                    >
                        gift for 45 year old man who loves football
                    </Button>
                </animated.div>
            )}

            <StaticQuery<GatsbyTypes.Query>
                query={`${indexPageStaticQuery}`}
                render={(data) => {
                    const node = get(data, 'prismic.allChat2orderhomes.edges[0].node');
                    const banner = node.banner.url;
                    const bannerTitle = node.banner_title;
                    const bannerSubTitle = node.banner_subtitle;
                    const bannerButtonText = node.banner_button_text;
                    const categorySectionTitle = get(
                        data,
                        'prismic.allChat2orderhomes.edges[0].node.category_section_title'
                    );
                    const categorySectionIntro = get(
                        data,
                        'prismic.allChat2orderhomes.edges[0].node.category_section_short_intro'
                    );
                    const categoryItems = get(
                        data,
                        'prismic.allChat2orderhomes.edges[0].node.category_block'
                    );			
                    const callUsBanner = node.call_us_banner;
                    const callUsTitle = node.call_us_title;
                    const callUsSubTitle = node.call_us_subtitle;
                    const callUsButtonText = node.call_us_button_text;

                    return (
                        <SecondaryLayout>
                            <SEO title="Home" />
                            <HomeBanner
                                banner={banner}
                                bannerTitle={bannerTitle}
                                bannerSubTitle={bannerSubTitle}
                                bannerButtonText={bannerButtonText}
                            />

                            {/* Gradient Box with Search */}
                            <Box
                                ref={searchSectionRef} // Attach the ref to the search section
                                sx={{
                                    background: 'linear-gradient(to right, purple, indigo)',
                                    padding: '20px',
                                    borderRadius: '8px',
                                    marginTop: '20px',
                                    textAlign: 'center',
                                }}
                            >
                                {/* Search Bar */}
                                <Box
                                    as="label"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'white',
                                        borderRadius: '8px',
                                        padding: '10px',
                                        maxWidth: '600px',
                                        margin: '0 auto',
                                    }}
                                >
                                    <svg
                                        className="search-icon"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        style={{ marginRight: '10px' }}
                                    >
                                        <path
                                            d="M16.122,12.452a6.772,6.772,0,1,0-1.195,1.2l.036.038,3.592,3.592a.847.847,0,0,0,1.2-1.2L16.16,12.488ZM14.364,4.706a5.079,5.079,0,1,1-7.183,0A5.079,5.079,0,0,1,14.364,4.706Z"
                                            transform="translate(-4 -1.525)"
                                            fillRule="evenodd"
                                        />
                                    </svg>
                                    <input
                                        type="text"
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        placeholder="What Are You Looking For?"
                                        autoComplete="off"
                                        style={{
                                            flex: 1,
                                            border: 'none',
                                            outline: 'none',
                                            fontSize: '16px',
                                        }}
                                    />
                                    {searchValue && (
                                        <>
                                            {/* Search Icon */}
                                            <svg
                                                className="search-icon"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 14 14"
                                                style={{ marginRight: '10px', cursor: 'pointer' }}
                                                onClick={() => {
                                                    console.log('Search triggered for:', searchValue);
                                                    navigate('/aisearch',{state: { searchString: searchValue}})
                                                }}
                                            >
                                                <path
                                                    d="M16.122,12.452a6.772,6.772,0,1,0-1.195,1.2l.036.038,3.592,3.592a.847.847,0,0,0,1.2-1.2L16.16,12.488ZM14.364,4.706a5.079,5.079,0,1,1-7.183,0A5.079,5.079,0,0,1,14.364,4.706Z"
                                                    transform="translate(-4 -1.525)"
                                                    fill="black"
                                                />
                                            </svg>

                                            {/* Close Icon */}
                                            <svg
                                                className="close-icon"
                                                onClick={() => setSearchValue('')}
                                                width="14"
                                                height="14"
                                                viewBox="0 0 14 14"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <path
                                                    d="M6.115,4.792A.936.936,0,0,0,4.792,6.115l5.4,5.4-5.4,5.4a.936.936,0,0,0,1.323,1.323l5.4-5.4,5.4,5.4a.936.936,0,0,0,1.323-1.323l-5.4-5.4,5.4-5.4a.936.936,0,1,0-1.323-1.323l-5.4,5.4Z"
                                                    transform="translate(-4.518 -4.518)"
                                                />
                                            </svg>
                                        </>
                                    )}
                                </Box>

                                {/* Pre-Populated Search Suggestion */}
                                <Button
                                    onClick={() => setSearchValue('gift for 45 year old man who loves football')}
                                    sx={{
                                        marginTop: '20px',
                                        backgroundColor: 'white',
                                        color: 'black',
                                        fontSize: '14px',
                                        padding: '10px 20px',
                                        borderRadius: '20px',
                                        cursor: 'pointer',
                                        border: 'none',
                                    }}
                                >
                                    gift for 45 year old man who loves football
                                </Button>
                            </Box>

                            <CategoryBlocks
                                title={categorySectionTitle}
                                shortIntro={categorySectionIntro}
                                categoryItems={categoryItems}
                            />					
                            <AllProducts withLink={true}/>					
                            <CallusBanner
                                callUsBanner={callUsBanner}
                                callUsTitle={callUsTitle}
                                callUsSubTitle={callUsSubTitle}
                                callUsButtonText={callUsButtonText}
                            />
                        </SecondaryLayout>
                    );
                }}
            />
        </>
    );
};

export default IndexPage;
